// ════════════════════════════════════════════════════════════════════════════
//  ManDana — moving an account into the engine
//  ---------------------------------------------------------------------------
//  This is the one file in the phase that picks up somebody's data and puts it
//  somewhere else. A year of a prayer record, a journal, whatever databases
//  they have built. There is no version of "mostly worked" that is acceptable
//  here, so the shape of it is: copy, then PROVE the copy, and only then write
//  down that it happened.
//
//  IT VERIFIES BY READING BACK. Counting the writes we issued is counting
//  intentions; the audit reads every document out of the database again and
//  compares it to the source key by key and byte by byte. That is the only
//  check that can see a value which arrived changed, which is the failure that
//  would otherwise be completely invisible — no error, no missing row, just a
//  document that is subtly not what was written.
//
//  A FAILED AUDIT LEAVES NO MARKER. So the next boot tries again, against a
//  localStorage that was never touched. That is also what "reversible" means in
//  practice, and it is worth being blunt about because the word usually implies
//  a restore: there is nothing to restore. This file never writes to the source
//  and never removes from it. Backing the whole thing out is declining to open
//  the engine, which is one condition in md-app.jsx.
//
//  THE MARKER LIVES IN THE DATABASE IT DESCRIBES. Not in localStorage beside
//  it. WebKit evicts origin storage after about seven days without a visit and
//  every browser evicts under pressure; when OPFS goes, the marker goes with
//  it, the next boot finds none, and the account is copied again from the side
//  that survived. Put the marker on the other side and that same eviction
//  produces an app that opens an empty database and believes it is full — which
//  is the worst outcome available anywhere in this phase, reachable by one
//  reasonable-looking decision.
//
//  localStorage IS NOT DELETED, here or in this phase. That instruction was
//  written down before any of this existed and it still holds:
//  it stays a read-only source until the engine has been the truth for long
//  enough that nobody is nervous.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MARKER = 'migration';

  type Audit = {
    ok: boolean;
    missing: string[];
    differing: string[];
    extra: string[];
    keys: number;
    bytes: number;
  };

  /* ── did everything arrive, unchanged? ────────────────────────────────────
     Pure, over two Maps, so the one judgement that matters can be proven
     without a database anywhere near it.

     `missing` and `differing` are losses and either one fails the audit.
     `extra` is not: a document in the destination that the source never had
     means something else is writing there, which somebody should know about,
     but nothing of the user's has gone. Failing on it would refuse a migration
     that lost nothing. */
  function audit(source: Map<string, string>, destination: Map<string, string>): Audit {
    const missing: string[] = [];
    const differing: string[] = [];
    const extra: string[] = [];
    let bytes = 0;

    for (const [key, value] of source) {
      bytes += key.length + (value == null ? 0 : value.length);
      /* has(), not a falsy check: '' is a document and `!value` eats it. */
      if (!destination.has(key)) missing.push(key);
      else if (destination.get(key) !== value) differing.push(key);
    }
    for (const key of destination.keys()) if (!source.has(key)) extra.push(key);

    return { ok: missing.length === 0 && differing.length === 0, missing, differing, extra, keys: source.size, bytes };
  }

  /* ── should this run at all? ──────────────────────────────────────────────
     Also pure. A marker written by an older schema, a marker we cannot parse,
     and no marker at all all mean the same thing: copy again. Copying is
     idempotent — every write is an upsert of identical bytes — so the safe
     answer and the cheap answer coincide, and there is no reason to be clever
     about a marker that looks odd. */
  function needed(marker: any, version: number): boolean {
    let parsed = marker;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { return true; }
    }
    if (!parsed || typeof parsed !== 'object') return true;
    return parsed.version !== version;
  }

  /* ── the migration ───────────────────────────────────────────────────────
     `source` and `engine` are passed in rather than reached for, the way
     mdBootGate takes its three steps: it is what lets the whole thing run
     against a real SQLite in a test without a Worker, and it keeps this file
     from knowing anything about either side beyond the four calls it makes.

     IT NEVER THROWS. A boot must not die here — the app is perfectly usable
     with the engine empty, because nothing reads from it yet. Every failure
     comes back as a value, and the marker is not written for any of them. */
  async function migrate(deps: any) {
    const source = (deps && deps.source) || { keys: () => g.mdStorageKeys(), readRaw: (k: string) => g.mdReadRaw(k) };
    const engine = (deps && deps.engine) || {
      apply: (w: any) => g.mdEngineApply(w),
      load: () => g.mdEngineLoad(),
      meta: (n: string) => g.mdEngineMeta(n),
      setMeta: (n: string, v: string) => g.mdEngineSetMeta(n, v),
    };
    const version = (deps && deps.version) || g.MD_ENGINE_SCHEMA_VERSION;

    try {
      const marker = await engine.meta(MARKER);
      if (!marker || marker.ok === false) {
        return { ok: false, migrated: false, error: (marker && marker.error) || 'the engine would not say whether it had been migrated' };
      }
      if (!needed(marker.value, version)) {
        return { ok: true, migrated: false, reason: 'already' };
      }

      /* Read the whole source once, into the Map the audit will compare
         against. Reading it a second time after the copy would compare the
         destination against a source that may have moved under it — a write
         landing mid-migration would then read as a loss. */
      const keys = source.keys();
      const original = new Map<string, string>();
      for (const key of keys) {
        const raw = source.readRaw(key);
        if (raw != null) original.set(key, raw);
      }

      const applied = await engine.apply(original);
      if (!applied || applied.ok === false) {
        return { ok: false, migrated: false, error: (applied && applied.error) || 'the engine refused the copy' };
      }

      const back = await engine.load();
      if (!back || back.ok === false) {
        return { ok: false, migrated: false, error: (back && back.error) || 'the engine would not read the copy back' };
      }

      const result = audit(original, new Map(back.docs));
      if (!result.ok) {
        /* Deliberately no marker. The next boot repeats this, and until one
           passes, localStorage remains the only thing anything reads. */
        return { ok: false, migrated: false, audit: result, keys: result.keys, bytes: result.bytes };
      }

      const stamped = await engine.setMeta(MARKER, JSON.stringify({
        version, at: Date.now(), keys: result.keys, bytes: result.bytes,
      }));
      if (!stamped || stamped.ok === false) {
        /* The data is there and audited; only the note failed. Say so — the
           next boot will copy the same bytes over themselves, which costs a
           moment and loses nothing. */
        return { ok: false, migrated: true, audit: result, error: (stamped && stamped.error) || 'the copy is complete but could not be recorded' };
      }

      return { ok: true, migrated: true, reason: 'copied', keys: result.keys, bytes: result.bytes, audit: result };
    } catch (err: any) {
      return { ok: false, migrated: false, error: (err && err.message) || String(err) };
    }
  }

  Object.assign(g, {
    mdMigrate: migrate,
    mdMigrationAudit: audit,
    mdMigrationNeeded: needed,
    MD_MIGRATION_MARKER: MARKER,
  });
})(typeof window !== 'undefined' ? window : globalThis);
